import React, { createContext, FC, ReactNode, useMemo } from 'react';

import { resolveAzureConfig } from './config/azureConfig';
import { AzureApi, createAzureApi } from './services/AzureApi';
import { AzureAuthClient, createAzureAuthClient } from './services/azureAuth';
import { AzureScreenshotService, createAzureScreenshotService } from './services/azureScreenshot';
import {
	AzureTicketCreatorConfig,
	ResolvedAzureTicketCreatorConfig
} from './types/AzureTicketCreatorConfig';

export interface AzureTicketCreatorContextValue {
	config: ResolvedAzureTicketCreatorConfig;
	auth: AzureAuthClient;
	api: AzureApi;
	screenshot: AzureScreenshotService;
}

export const AzureTicketCreatorContext = createContext<AzureTicketCreatorContextValue | null>(null);

interface IAzureTicketCreatorProviderProps {
	config: AzureTicketCreatorConfig;
	children: ReactNode;
}

/**
 * Builds the module's resolved config plus its auth client and REST api instance, and exposes
 * them via context. Everything lives in component state (a memo) — there is no module-level
 * singleton or global config. Keyed on the config's primitive fields (not object identity), so an
 * inline `config={{ ... }}` literal still builds the auth/api instances once and not per render.
 */
export const AzureTicketCreatorProvider: FC<IAzureTicketCreatorProviderProps> = ({
	config,
	children
}) => {
	const { clientId, redirectUri, apiBaseUrl, usersBaseUrl, logger, appElement } = config;
	const value = useMemo<AzureTicketCreatorContextValue>(() => {
		const resolved = resolveAzureConfig({
			clientId,
			redirectUri,
			apiBaseUrl,
			usersBaseUrl,
			logger,
			appElement
		});
		const auth = createAzureAuthClient(resolved);
		const api = createAzureApi({
			auth,
			logger: resolved.logger,
			apiBaseUrl: resolved.apiBaseUrl,
			usersBaseUrl: resolved.usersBaseUrl
		});
		const screenshot = createAzureScreenshotService();
		return { config: resolved, auth, api, screenshot };
	}, [clientId, redirectUri, apiBaseUrl, usersBaseUrl, logger, appElement]);

	return (
		<AzureTicketCreatorContext.Provider value={value}>
			{children}
		</AzureTicketCreatorContext.Provider>
	);
};
